import React, { useEffect, useMemo, useState } from "react";
import api from "../auth/api";

function cls(...a) {
  return a.filter(Boolean).join(" ");
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return String(dateStr).slice(0, 10);
    return d.toLocaleDateString();
  } catch {
    return String(dateStr).slice(0, 10);
  }
}

function fmtTime(hora) {
  if (!hora) return "";
  const s = String(hora);
  const m = s.match(/^(\d{2}:\d{2}:\d{2})/);
  return m ? m[1] : s.slice(0, 8);
}

export default function SiniestralidadModal({
  open,
  onClose,
  contrato = "",
  pageSizeDefault = 10,
}) {
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [perfil, setPerfil] = useState("");
  const [telefono, setTelefono] = useState("");
  const [codigoTipoServicio, setCodigoTipoServicio] = useState("");
  const [order, setOrder] = useState("desc");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizeDefault);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setRows([]);
    setPagination(null);
    setPage(1);
    setQ("");
    setDateFrom("");
    setDateTo("");
    setPerfil("");
    setTelefono("");
    setCodigoTipoServicio("");
    setOrder("desc");
    setPageSize(pageSizeDefault);
  }, [open, pageSizeDefault, contrato]);

  const canQuery = useMemo(() => {
    return !!String(contrato || "").trim();
  }, [contrato]);

  const fetchData = async (opts = {}) => {
    if (!canQuery) return;

    setLoading(true);
    setErr("");

    const p = opts.page ?? page;
    const ps = opts.pageSize ?? pageSize;

    try {
      const { data } = await api.get(
        `/contratos/${encodeURIComponent(String(contrato).trim())}/siniestralidad`,
        {
          params: {
            page: p,
            pageSize: ps,
            q: q || undefined,
            dateFrom: dateFrom || undefined,
            dateTo: dateTo || undefined,
            perfil: perfil || undefined,
            telefono: telefono || undefined,
            codigoTipoServicio: codigoTipoServicio || undefined,
            order: order || "desc",
          },
        }
      );

      setRows(data?.rows || []);
      setPagination(data?.pagination || null);
    } catch (e) {
      setRows([]);
      setPagination(null);
      setErr(e?.response?.data?.error || e?.message || "Error consultando siniestralidad");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || !canQuery) return;

    const t = setTimeout(() => {
      setPage(1);
      fetchData({ page: 1 });
    }, 250);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, canQuery, q, dateFrom, dateTo, perfil, telefono, codigoTipoServicio, order, pageSize]);

  useEffect(() => {
    if (!open || !canQuery) return;
    fetchData({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const pages = pagination?.pages || 1;
  const total = pagination?.total || 0;

  const pageButtons = useMemo(() => {
    const out = [];
    const max = pages;
    const cur = page;
    const start = Math.max(1, cur - 3);
    const end = Math.min(max, cur + 3);

    if (start > 1) out.push(1);
    if (start > 2) out.push("…");
    for (let i = start; i <= end; i++) out.push(i);
    if (end < max - 1) out.push("…");
    if (end < max) out.push(max);

    return out;
  }, [page, pages]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        onClick={() => onClose?.()}
      />

      <div className="relative w-[1200px] max-w-[95vw] rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 overflow-hidden animate-[modalIn_.14s_ease-out]">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="font-extrabold text-lg text-slate-900">
              Siniestralidad
            </div>
            <div className="text-sm text-slate-600 mt-0.5 truncate">
              Contrato: <b className="text-slate-900">{String(contrato || "—")}</b>
              {pagination ? (
                <>
                  {" "}· Total: <b className="text-slate-900">{total}</b>
                </>
              ) : null}
            </div>
          </div>

          <div className="flex gap-2 shrink-0">
            <button
              type="button"
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-900 hover:bg-slate-50 active:scale-[.98]"
              onClick={() => fetchData({ page })}
              title="Refrescar"
              disabled={loading || !canQuery}
            >
              ⟳
            </button>

            <button
              type="button"
              className="px-3 py-1.5 rounded-xl border border-slate-200 text-slate-900 hover:bg-slate-50 active:scale-[.98]"
              onClick={() => onClose?.()}
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-5 py-4 border-b bg-white">
          {!canQuery ? (
            <div className="text-sm text-red-600 font-semibold">
              Este contacto no tiene número de contrato. No se puede consultar siniestralidad.
            </div>
          ) : (
            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 md:col-span-3">
                <label className="text-xs font-extrabold text-slate-700">Buscar</label>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Perfil, descripción, código o teléfono..."
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Perfil</label>
                <input
                  value={perfil}
                  onChange={(e) => setPerfil(e.target.value)}
                  placeholder="Ej: MEDICO"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-2">
                <label className="text-xs font-extrabold text-slate-700">Teléfono</label>
                <input
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                  placeholder="0999999999"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-1">
                <label className="text-xs font-extrabold text-slate-700">Código</label>
                <input
                  value={codigoTipoServicio}
                  onChange={(e) => setCodigoTipoServicio(e.target.value)}
                  placeholder="12"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                />
              </div>

              <div className="col-span-6 md:col-span-1">
                <label className="text-xs font-extrabold text-slate-700">Orden</label>
                <select
                  value={order}
                  onChange={(e) => setOrder(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                >
                  <option value="desc">Reciente</option>
                  <option value="asc">Antiguo</option>
                </select>
              </div>

              <div className="col-span-6 md:col-span-1">
                <label className="text-xs font-extrabold text-slate-700">Tamaño</label>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-slate-900 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-300"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="p-5 bg-slate-50">
          {err ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700 font-semibold mb-4">
              {err}
            </div>
          ) : null}

          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="grid grid-cols-14 gap-0 px-4 py-3 border-b bg-slate-50 text-xs font-extrabold text-slate-600">
              <div className="col-span-2">Fecha</div>
              <div className="col-span-2">Hora</div>
              <div className="col-span-3">Perfil</div>
              <div className="col-span-2">Teléfono</div>
              <div className="col-span-2">Código</div>
              <div className="col-span-3">Descripción</div>
            </div>

            <div className="max-h-[52vh] overflow-y-auto">
              {loading ? (
                <div className="p-6 text-slate-600">Cargando…</div>
              ) : rows.length === 0 ? (
                <div className="p-6 text-slate-500">No hay registros de siniestralidad con esos filtros.</div>
              ) : (
                rows.map((r, idx) => (
                  <div
                    key={`${idx}_${r.fecha}_${r.hora}_${r.codigoTipoServicio}_${r.telefono || ""}`}
                    className="grid grid-cols-14 gap-0 px-4 py-3 border-b last:border-b-0 hover:bg-slate-50"
                  >
                    <div className="col-span-2 text-sm text-slate-900 font-semibold">
                      {fmtDate(r.fecha)}
                    </div>

                    <div className="col-span-2 text-sm text-slate-700">
                      {fmtTime(r.hora)}
                    </div>

                    <div className="col-span-3 text-sm text-slate-800">
                      {r.perfil || "—"}
                    </div>

                    <div className="col-span-2 text-sm text-slate-700">
                      {r.telefono || "—"}
                    </div>

                    <div className="col-span-2">
                      <span
                        className={cls(
                          "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-bold",
                          "border-slate-200 text-slate-700 bg-white"
                        )}
                      >
                        {r.codigoTipoServicio || "—"}
                      </span>
                    </div>

                    <div className="col-span-3 text-sm text-slate-800 whitespace-pre-wrap">
                      {r.descripcion || "—"}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-600">
              Página <b className="text-slate-900">{page}</b> de{" "}
              <b className="text-slate-900">{pages}</b>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={loading || page <= 1}
              >
                ←
              </button>

              {pageButtons.map((b, i) =>
                b === "…" ? (
                  <span key={`dots_${i}`} className="px-2 text-slate-500">…</span>
                ) : (
                  <button
                    key={`p_${b}`}
                    className={cls(
                      "px-3 py-1.5 rounded-xl border font-extrabold",
                      b === page
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-slate-900 border-slate-200 hover:bg-slate-50"
                    )}
                    onClick={() => setPage(Number(b))}
                    disabled={loading}
                  >
                    {b}
                  </button>
                )
              )}

              <button
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-900 font-bold disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(pages, p + 1))}
                disabled={loading || page >= pages}
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { transform: translateY(6px) scale(.985); opacity: 0; }
          to   { transform: translateY(0) scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}